import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import Moment from 'moment';

import { get as _get } from 'lodash';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import Timeline from './timeline';
import Mitigations from './mitigations';
import FileUpload from './upload';
import Logs from './logs';
import Notes from './notes';
import IncidentData from './data';

import ConfirmationDialog from '../../../components/confirmationdlg';

import { pointlessPink, purple } from '../../../data/colors';
import Auth, { ProfileContext } from '../../../data/auth-service';
import IncidentService from '../../../data/incident-service';
import DataContext from '../../../data/data-context';
import UsersService from '../../../data/users-service';

const useStyles = makeStyles(theme => ({
    card: {
        maxWidth: 345,
    },
    assignee: {
        fontSize: '16px',
        marginBottom: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    status: {
        fontSize: '16px',
        color: theme.palette.secondary.main,
        '& span': {
            '&.new': {
                color: pointlessPink[200],
            },
            '&.open': {
                color: theme.palette.primary.main,
            },
            '&.resolved': {
                color: purple[300],
            },
        }
    },
    mitre: {
        fontSize: '16px',
        marginBottom: theme.spacing(1),
        color: theme.palette.secondary.main,
        '& span': {
            color: purple[300],
        },
    },
    severity: {
        fontSize: '16px',
        color: theme.palette.secondary.main,
        '& span': {
            color: theme.palette.error.main,
        },
    },
    tagged: {
        color: theme.palette.primary.main,
    },
    highlight: {
        fontSize: '16px',
        marginBottom: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    tabBar: {
        marginTop: theme.spacing(1),
    },
    secondary: {
        color: theme.palette.secondary.main,
    },
    resolveDialog: {
        '& .MuiDialog-container.MuiDialog-scrollPaper': {
            '& .MuiPaper-root.MuiDialog-paper': {
                maxWidth: '550px',
                width: '550px',
            }
        }
    },
    select: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(2),
        marginTop: -5,
        width: '100%',
    },
    severitySelect: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        marginTop: -5,
        width: '100px',
    },
}));

const IncidentResponse = ({ perm }) => {
    const classes = useStyles();
    const theme = useTheme();
    const params = useParams();
    const navigate = useNavigate();
    const [tab, setTab] = useState(0);
    const [resolveOpen, setResolveOpen] = useState(false);
    const [reopenOpen, setReopenOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [incident, setIncident] = useState();
    const [closingRemarks, setClosingRemarks] = useState('');
    const [users, setUsers] = useState([]);
    const [collapseSummary, setCollapseSummary] = useState(true);
    const {
        setAlertMitre,
        setMtdtMitre,
        clearMtdtMitre,
        clearAlertMitre,
        mitreCategoriesMap,
    } = useContext(DataContext);
    const readAlert = Auth.permissions.get('Alerts').read;
    const readUser = Auth.permissions.get('Users').read;
    const { updateProfile, getProfile } = useContext(ProfileContext);
    const incidentPerms = Auth.permissions.get('Incidents');

    useEffect(() => {
        getIncident();
        if (readUser) {
            getUsers();
        }

        const profile = getProfile('incidents');
        setCollapseSummary(_get(profile, 'collapseSummary', true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toggleCollapseSummary = () => {
        setCollapseSummary(current => !current);

        const profile = getProfile('incidents');
        updateProfile('incidents', { ...profile, collapseSummary: !collapseSummary });
    };

    const getIncident = () => {
        IncidentService.get(params.uuid).then(res => {
            setIncident(res);
        }, err => {
            console.error(err);
            toast.error('Error retrieving incident: ', err);
        });
    };

    const getUsers = () => {
        UsersService.getUsers().then(res => {
            setUsers(res.data);
        });
    };

    const handleTabChange = (e, t) => {
        setTab(t);
    };

    const handleResolve = () => {
        handleToggleResolve();
        const i = { ...incident };
        if (!i.data.comments) i.data.comments = [];
        if (closingRemarks) {
            i.data.comments.push(closingRemarks);
        }
        IncidentService.update(incident.uuid, i).then(() => {
            IncidentService.resolve(incident.uuid).then(res => {
                setIncident(res);
                toast.success('Incident resolved successfully');
            }, err => {
                console.error(err);
                toast.error('Error resolving incident: ', err);
            });
        }, err => {
            toast.error('Error adding closing comment. Unable to resolve incident at this time. Please try again...');
            console.error(err);
        });
    };

    const handleToggleResolve = () => {
        setResolveOpen(!resolveOpen);
    };

    const handleReopen = () => {
        handleToggleReopen();

        IncidentService.open(incident.uuid).then(res => {
            setIncident(res);
            toast.success('Incident has been reopened');
        }, err => {
            console.error(err);
            toast.error('Error reopening incident: ', err);
        });
    };

    const handleToggleReopen = () => {
        setReopenOpen(!reopenOpen);
    };

    const handleUpdate = (data) => {
        IncidentService.update(incident.uuid, data).then(res => {
            setIncident(res);
            toast.success('Incident updated successfully');
        }, err => {
            console.error(err);
            toast.error('Error updating incident: ', err);
        });
    };

    const handleDelete = () => {
        handleToggleDelete();
        IncidentService.delete(params.uuid).then(() => {
            toast.success('Incident deleted successfully');
            navigate(-1);
        }, err => {
            console.error(err);
            toast.error('Error deleting incident: ', err);
        });
    };

    const handleOnUpdate = data => {
        IncidentService.update(params.uuid, data).then(res => {
            setIncident(res);
        }, err => {
            toast.error(err);
        });
    };

    const handleOnKick = () => {
        IncidentService.get(params.uuid).then(res => {
            setIncident(res);
        }, err => {
            toast.error(err);
        });
    };

    const handleOnRemoveData = data => {
        IncidentService.removeData(params.uuid, data).then(res => {
            setIncident(res);
        }, err => {
            toast.error(err);
        });
    };

    const handleOnUnlinkData = (typ, uuid) => {
        if (typ === 'metadata') {
            IncidentService.removeMetadata(params.uuid, uuid).then(res => {
                setIncident(res);
            }, err => {
                toast.error(err);
            });
        } else {
            IncidentService.removeAlert(params.uuid, uuid).then(res => {
                setIncident(res);
            }, err => {
                toast.error(err);
            });
        }
    };

    const handleOnSetMitre = ({ type, uuid, mitre }) => {
        const fn = type === 'metadata' ? setMtdtMitre : setAlertMitre;
        fn(uuid, mitre).then(() => {
            getIncident();
        }, err => {
            toast.error(err);
        });
    };

    const handleOnRemoveMitre = ({ type, uuid }) => {
        const fn = type === 'metadata' ? clearMtdtMitre : clearAlertMitre;
        fn(uuid).then(() => {
            getIncident();
        }, err => {
            toast.error(err);
        });
    };

    const handleToggleDelete = () => {
        setDeleteOpen(!deleteOpen);
    };

    const handleUpdateClosingRemarks = (e) => {
        setClosingRemarks(e.target.value);
    };

    const handleEditAssignee = (e) => {
        const i = { ...incident };
        i.assigner = e.target.value;
        IncidentService.update(incident.uuid, i).then(res => {
            setIncident(res);
            toast.success('Assignee updated successfully');
        }, () => {
            toast.error('Unable to update assignee');
        });
    };

    const handleEditSeverity = (e) => {
        const i = { ...incident };
        i.data.severity = e.target.value;
        IncidentService.update(incident.uuid, i).then(res => {
            setIncident(res);
            toast.success('Severity updated');
        }, () => {
            toast.error('Unable to update severity');
        });
    };

    return incident ? (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box mt={-5} display="flex" justifyContent="flex-end">
                    <Box ml={2}>
                        {incidentPerms.admin && <Box display={incident.status === 'resolved' ? 'block' : 'none'}>
                            <Button variant="contained" color="primary" onClick={handleToggleReopen}>Reopen</Button>
                        </Box>}
                        {incidentPerms.admin && <Box display={incident.status === 'resolved' ? 'none' : 'block'}>
                            <Button variant="contained" color="primary" onClick={handleToggleResolve}>Resolve</Button>
                        </Box>}
                    </Box>
                    {incidentPerms.delete && <Box ml={2}><Button variant="contained" color="secondary" onClick={handleToggleDelete}><DeleteIcon /> Delete</Button></Box>}
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Paper elevation={3}>
                    <Box p={2} style={{ position: 'relative', paddingBottom: collapseSummary ? 5 : 16 }}>
                        <Grid container>
                            <Grid item xs={3} style={{ overflow: 'hidden' }}>
                                <Box className={classes.assignee} display={readUser ? 'none' : 'flex'}>
                                    Assignee: <span style={{ marginLeft: theme.spacing(1) }}>{incident.assigner}</span>
                                </Box>
                                <Box className={classes.assignee} display={readUser ? 'flex' : 'none'}>
                                    Assignee: <span style={{
                                        display: incident.status === 'resolved' ? 'block' : 'none',
                                        marginLeft: theme.spacing(1),
                                    }}>{incident.assigner}</span>
                                    <Select
                                        disabled={!incidentPerms.write}
                                        variant="standard"
                                        onChange={handleEditAssignee}
                                        value={incident.assigner}
                                        className={classes.select}
                                        IconComponent={!incidentPerms.write ? null : ArrowDropDownIcon}
                                        style={{
                                            display: incident.status === 'resolved' ? 'none' : 'block'
                                        }}
                                    >
                                        {users.map((v, i) => {
                                            return (
                                                <MenuItem key={'assignee-user-' + i} value={v.username}>{`${v.username} - ${v.name}`}</MenuItem>
                                            );
                                        })}
                                    </Select>
                                </Box>
                            </Grid>
                            <Grid item xs={5} style={{ paddingLeft: 20 }}>
                                <Box className={classes.mitre}>
                                    Mitre ATT&CK:
                                    <span>
                                        {mitreCategoriesMap[incident.mitre_category_id]
                                            ? mitreCategoriesMap[incident.mitre_category_id].name
                                            : incident.mitre_category_id}
                                    </span>
                                </Box>
                            </Grid>
                            <Grid item xs={4}>
                                <Box className={classes.highlight}>Start: {Moment(incident.start).format(theme.date.long)}</Box>
                                {/* <Box className={classes.tagged}>Tagged: </Box> */}
                            </Grid>
                            <Collapse in={!collapseSummary} style={{ width: '100%' }}>
                                <Grid container>
                                    <Grid item xs={3}>
                                        <Box className={classes.status}>Status: <span className={(incident.status || '').toLowerCase()}>{incident.status}</span></Box>
                                    </Grid>
                                    <Grid item xs={5} style={{ paddingLeft: 20 }}>
                                        <Box className={classes.highlight}>Date Created: {Moment(incident.created).format(theme.date.long)}</Box>
                                        <Box className={classes.severity} display="flex">
                                            Severity:
                                            <span style={{
                                                display: incident.status === 'resolved' ? 'block' : 'none',
                                                marginLeft: theme.spacing(1),
                                            }}>{(incident.data || {}).severity}</span>
                                            <Select
                                                disabled={!incidentPerms.write}
                                                variant="standard"
                                                onChange={handleEditSeverity}
                                                value={incident.data.severity}
                                                className={classes.severitySelect}
                                                IconComponent={!incidentPerms.write ? null : ArrowDropDownIcon}
                                                style={{
                                                    display: incident.status === 'resolved' ? 'none' : 'block'
                                                }}
                                            >
                                                <MenuItem value={1}>1</MenuItem>
                                                <MenuItem value={2}>2</MenuItem>
                                                <MenuItem value={3}>3</MenuItem>
                                                <MenuItem value={4}>4</MenuItem>
                                            </Select>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={4}>
                                        {incident.resolved ? <Box className={classes.highlight}>End: {Moment(incident.resolved).format(theme.date.long)}</Box> : null}
                                        {/* <Box className={classes.tagged}>Tagged: </Box> */}
                                    </Grid>
                                </Grid>
                            </Collapse>
                        </Grid>
                        <Box style={{ position: 'absolute', right: 10, top: 0 }}>
                            <IconButton
                                color="primary"
                                onClick={toggleCollapseSummary}
                                aria-label={collapseSummary ? 'show summary' : 'hide summary'}
                                size="large">
                                {collapseSummary
                                    ? <ExpandMoreIcon fontSize="inherit"/>
                                    : <ExpandLessIcon fontSize="inherit"/>
                                }
                            </IconButton>
                        </Box>
                    </Box>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <AppBar position="static" className={classes.tabBar}>
                    <Tabs value={tab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                        <Tab aria-label="Incident Timeline" label="Incident Timeline" value={0} />
                        {readAlert && <Tab aria-label="Data" label="Data" value={5} />}
                        <Tab aria-label="Mitigations" label="Mitigations" value={1} />
                        <Tab aria-label="File Upload" label="File Upload" value={2} />
                        <Tab aria-label="Incident Log" label="Incident Log" value={3} />
                        <Tab aria-label="Notes" label="Notes" value={4} />
                    </Tabs>
                </AppBar>
                <Box className={classes.fullWidth} value={tab} index={0} hidden={tab !== 0}>
                    {/* kick the timeline */}
                    {tab === 0 && <Timeline
                        incident={incident}
                        onUpdate={handleOnUpdate}
                        onRemoveData={handleOnRemoveData}
                        onUnlinkData={handleOnUnlinkData}
                        onSetMitre={handleOnSetMitre}
                        onRemoveMitre={handleOnRemoveMitre}/>}
                </Box>
                <Box className={classes.fullWidth} value={tab} index={5} hidden={tab !== 5}>
                    {/* kick the data tables */}
                    {tab === 5 && readAlert && <IncidentData
                        incident={incident}
                        collapsed={collapseSummary}
                        onUpdate={handleOnUpdate}
                        onRemoveData={handleOnRemoveData}/>}
                </Box>
                <Box className={classes.fullWidth} value={tab} index={1} hidden={tab !== 1}>
                    <Mitigations
                        incident={incident}
                        onTabChange={handleTabChange}
                        onIncidentChange={handleUpdate}
                    />
                </Box>
                <Box className={classes.fullWidth} value={tab} index={2} hidden={tab !== 2}>
                    <FileUpload incident={incident} onKick={handleOnKick} />
                </Box>
                <Box className={classes.fullWidth} value={tab} index={3} hidden={tab !== 3}>
                    <Logs incident={incident} />
                </Box>
                <Box className={classes.fullWidth} value={tab} index={4} hidden={tab !== 4}>
                    <Notes incident={incident} onUpdate={handleOnUpdate} onRemoveData={handleOnRemoveData}/>
                </Box>
            </Grid>
            <ConfirmationDialog
                open={resolveOpen}
                title="Resolve Incident"
                message={(() => {
                    return (
                        <Box>
                            Are you sure you want to resolve <span className={classes.secondary}>{incident.name}</span>?
                            <Box mt={3}>
                                <Typography variant="subtitle2">Comment</Typography>
                                <Box bgcolor={theme.palette.background.default}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={6}
                                        value={closingRemarks}
                                        onChange={handleUpdateClosingRemarks}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    );
                })()}
                cancelLabel="Cancel"
                okLabel="Yes"
                onClose={handleToggleResolve}
                onOK={handleResolve}
                className={classes.resolveDialog}
            />
            <ConfirmationDialog
                open={reopenOpen}
                title="Reopen Incident"
                message={(() => {
                    return (
                        <Box>
                            Are you sure you want to reopen <span className={classes.secondary}>{incident.name}</span>?
                        </Box>
                    );
                })()}
                cancelLabel="Cancel"
                okLabel="Yes"
                onClose={handleToggleReopen}
                onOK={handleReopen}
            />
            <ConfirmationDialog
                open={deleteOpen}
                title="Delete incident"
                message={(() => {
                    return (
                        <Box>
                            Are you sure you want to delete <span className={classes.secondary}>{incident.name}</span>?
                        </Box>
                    );
                })()}
                cancelLabel="Cancel"
                okLabel="Yes"
                onClose={handleToggleDelete}
                onOK={handleDelete}
            />
        </Grid>
    ) : null;
};

IncidentResponse.propTypes = {
    perm: PropTypes.func,
};

export default IncidentResponse;
